import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingButtonOption from 'template-collectibles/publishing-ui/TemplatePublishingButtonOption';
import {
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  PUBLISHABLE_VERSION_STATUS_TO_ICON,
  PublishableVersionAction,
  PublishableVersionStatus,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import TemplatePublishingModal, {
  TemplatePublishingModalProps,
} from 'template-collectibles/publishing-ui/TemplatePublishingModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {readableTime} from 'util/DateUtil';
import {capitalize} from 'util/NameUtil';

function runTemplatePublishingModalPublish(
  publishableModelTypeName: string,
  publishingRecords: Array<PublishingRecordForButton>,
  onPublish: (versionName: string, versionDescription?: string) => void
): void {
  legacyRunModal<TemplatePublishingModalProps>(TemplatePublishingModal, {
    publishingAction: PublishableVersionAction.PUBLISH,
    publishableModelTypeName,
    onRequestSubmit: ({versionName, versionDescription}) => {
      onPublish(versionName, versionDescription);
    },
    publishingRecords,
  });
}

function runTemplatePublishingModalWithdraw(
  publishableModelTypeName: string,
  publishingRecords: Array<PublishingRecordForButton>,
  onPublish: () => void
): void {
  legacyRunModal<TemplatePublishingModalProps>(TemplatePublishingModal, {
    publishingAction: PublishableVersionAction.WITHDRAW,
    publishableModelTypeName,
    onRequestSubmit: onPublish,
    publishingRecords,
  });
}

function getButtonText(
  status: PublishableVersionStatus,
  versionModifiedAt: string,
  versionName?: string
): string {
  switch (status) {
    case PublishableVersionStatus.DRAFT:
      return 'Draft';
    case PublishableVersionStatus.DRAFT_NON_CURRENT:
      return readableTime(versionModifiedAt);
    default:
      return versionName!;
  }
}

export interface TemplatePublishingButtonProps {
  onCreateDraft: () => void;
  onPublish: (versionName: string, versionDescription?: string) => void;
  onWithdraw: () => void;
  publishableModelTypeName: string;
  publishingRecords: Array<PublishingRecordForButton>;
  isPublishOptionDisabled?: boolean;
  status: PublishableVersionStatus;
  versionModifiedAt: string;
  versionName?: string;
  tooltipPlacement?: FloatingPlacement;
}

function TemplatePublishingButton({
  onCreateDraft,
  onPublish,
  onWithdraw,
  publishableModelTypeName,
  publishingRecords,
  isPublishOptionDisabled,
  status,
  versionModifiedAt,
  versionName,
  tooltipPlacement,
}: TemplatePublishingButtonProps): JSX.Element {
  const createDraftOption = (
    <Option
      onSelect={onCreateDraft}
      key="template-publishing-option-createdraft"
      dataTestAttrs={{component: 'TemplatePublishingButton', element: 'menu', key: 'draft'}}
    >
      <TemplatePublishingButtonOption publishableVersionAction={PublishableVersionAction.CREATE_DRAFT} />
    </Option>
  );
  const publishOption = (
    <Option
      onSelect={() => {
        runTemplatePublishingModalPublish(
          publishableModelTypeName,
          publishingRecords,
          (versionName: string, versionDescription?: string) => {
            onPublish(versionName, versionDescription);
          }
        );
      }}
      isDisabled={isPublishOptionDisabled}
      dataTestAttrs={{component: 'TemplatePublishingButton', element: 'menu', key: 'publish'}}
      key="template-publishing-option-publish"
    >
      <TemplatePublishingButtonOption
        publishableVersionAction={PublishableVersionAction.PUBLISH}
        isDisabled={isPublishOptionDisabled}
      />
    </Option>
  );
  const withdrawOption = (
    <Option
      onSelect={() => {
        runTemplatePublishingModalWithdraw(publishableModelTypeName, publishingRecords, onWithdraw);
      }}
      key="template-publishing-option-withdraw"
      dataTestAttrs={{component: 'TemplatePublishingButton', element: 'menu', key: 'withdraw'}}
    >
      <TemplatePublishingButtonOption publishableVersionAction={PublishableVersionAction.WITHDRAW} />
    </Option>
  );

  let dropdownOptions: Array<JSX.Element> = [];
  switch (status) {
    case PublishableVersionStatus.DRAFT:
      dropdownOptions = [publishOption];
      break;
    case PublishableVersionStatus.DRAFT_NON_CURRENT:
      dropdownOptions = [createDraftOption];
      break;
    case PublishableVersionStatus.EFFECTIVE:
      dropdownOptions = [createDraftOption, withdrawOption];
      break;
    case PublishableVersionStatus.SUPERSEDED:
      dropdownOptions = [createDraftOption];
      break;
    case PublishableVersionStatus.WITHDRAWN:
      dropdownOptions = [createDraftOption];
      break;
    default:
      break;
  }

  const iconColor = PUBLISHABLE_VERSION_STATUS_TO_COLOR[status];
  const iconName = PUBLISHABLE_VERSION_STATUS_TO_ICON[status];
  const tooltipText = status === PublishableVersionStatus.DRAFT_NON_CURRENT ? 'Draft' : capitalize(status);

  return (
    <Tooltip text={tooltipText} placement={tooltipPlacement}>
      <Dropdown
        closeOnInsideClick={true}
        menu={
          <Menu dropLeft={true} width={MenuWidth.FIT_CONTENT}>
            {dropdownOptions}
          </Menu>
        }
      >
        <Button
          aria-label={tooltipText}
          size={ButtonSize.MEDIUM}
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          className={css(styles.buttonContainer)}
          dataTestAttrs={{component: 'TemplatePublishingButton', element: 'button'}}
        >
          <span className={css(styles.spanContainer)} style={{color: iconColor}}>
            <BenchlingIcon
              icon={iconName}
              dataTestAttrs={{component: 'TemplatePublishingButton', element: 'BenchlingIcon', key: iconName}}
            />
          </span>
          <span className={css(styles.spanContainer)}>
            {getButtonText(status, versionModifiedAt, versionName)}
          </span>
          <span className={css(styles.spanContainer, styles.chevronIcon)}>
            <BenchlingIcon icon="chevron-down" />
          </span>
        </Button>
      </Dropdown>
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    padding: '8px 12px',
    borderRadius: 16,
    border: `1px solid ${Color.GRAY_700}`,
    fontSize: 14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Color.GRAY_900,
    ':hover': {
      background: Color.GRAY_50,
    },
    ':focus': {
      background: Color.BLUE_100,
    },
    height: '32px',
  },
  spanContainer: {
    marginLeft: '0.2em',
    marginRight: '0.2em',
  },
  chevronIcon: {
    color: Color.GRAY_900,
  },
});

export default TemplatePublishingButton;
